import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Bell, Download, Eye, X, Loader2, Crown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { downloadMedia } from "@/lib/downloadUtils";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { SignatureLoader } from "@/components/SignatureLoader";
import { extractCameraCode } from "@/components/admin/LightroomHelpDialog";

interface Notification {
 id: string;
 message: string;
 read: boolean;
 created_at: string;
 link?: string;
 type?: string;
 metadata?: {
 filenames?: string[];
 buyer_name?: string;
 order_id?: string;
 image_url?: string;
 };
}

// ─── Plan detection & theming ─────────────────────────────────────────────────
const getPlanInfo = (notification: Notification) => {
  const msg = (notification.message || '').toUpperCase();
  const type = (notification.type || '').toLowerCase();
  const isVip      = type === 'vip_upgrade' || msg.includes('VIP') || msg.includes('ASSINATURA');
  const isLate     = type === 'sale_late'   || msg.includes('ATRASADA');
  const isSale     = type === 'sale_complete' || type === 'sale_late' || msg.includes('VENDA') || msg.includes('COMPROU');
  const isLunar    = msg.includes('LUNAR');
  const isEstrela  = msg.includes('ESTRELA') || msg.includes('STAR');
  const isBasico   = msg.includes('BÁSICO') || msg.includes('BASICO') || msg.includes('BASIC');
  const isRenewal  = msg.includes('RENOVA') || msg.includes('RENOVOU');
  const colors = isLate
    ? { glow:'rgba(234,179,8,0.7)', shadow:'rgba(234,179,8,0.4)', border:'#EAB308', blur:'rgba(234,179,8,0.3)', text:'text-yellow-400' }
    : isLunar
    ? { glow:'rgba(100,120,220,0.7)', shadow:'rgba(100,120,220,0.4)', border:'#6478DC', blur:'rgba(100,120,220,0.3)', text:'text-indigo-300' }
    : isEstrela
    ? { glow:'rgba(255,200,50,0.7)', shadow:'rgba(255,200,50,0.4)', border:'#FFC832', blur:'rgba(255,200,50,0.3)', text:'text-amber-300' }
    : isBasico
    ? { glow:'rgba(80,160,255,0.7)', shadow:'rgba(80,160,255,0.4)', border:'#50A0FF', blur:'rgba(80,160,255,0.3)', text:'text-blue-300' }
    : isVip
    ? { glow:'rgba(255,215,0,0.7)', shadow:'rgba(255,215,0,0.4)', border:'#FFD700', blur:'rgba(255,215,0,0.3)', text:'text-yellow-300' }
    : isSale
    ? { glow:'rgba(255,106,0,0.7)', shadow:'rgba(255,106,0,0.4)', border:'#FF6A00', blur:'rgba(255,106,0,0.3)', text:'text-orange-400' }
    : { glow:'rgba(255,106,0,0.6)', shadow:'rgba(255,106,0,0.3)', border:'#FF6A00', blur:'rgba(255,106,0,0.25)', text:'text-orange-400' };
  const floatingIcons = isLunar   ? ['🌙','🌙','✨','⭐']
    : isEstrela                   ? ['⭐','⭐','✨','💫']
    : isBasico                    ? ['🔵','💧','✨','🔵']
    : isRenewal                   ? ['🔄','✨','💫','🔄']
    : isVip                       ? ['👑','✨','💫','👑']
    : isSale                      ? ['📸','💸','📸','💰']
    :                               ['🔔','✨','🔔','💫'];
  const planIcon = isLunar?'🌙':isEstrela?'⭐':isBasico?'🔵':isRenewal?'🔄':isVip?'👑':isSale?'📸':'🔔';
  const headline = isLate?'⚠️ VENDA ATRASADA!':isRenewal?'🔄 RENOVAÇÃO VIP!':isLunar?'🌙 NOVA ASSINATURA LUNAR!':isEstrela?'⭐ NOVA ASSINATURA ESTRELA!':isBasico?'🔵 NOVA ASSINATURA BÁSICA!':isVip?'👑 NOVO ASSINANTE VIP!':isSale?'📸 NOVA VENDA!':'NOVA NOTIFICAÇÃO!';
  return { isVip, isLate, isSale, isLunar, isEstrela, isBasico, isRenewal, colors, floatingIcons, planIcon, headline };
};

const SaleNotificationItem = ({ notification, onRemove }: { notification: Notification, onRemove: () => void }) => {
  useEffect(() => {
    const t = setTimeout(onRemove, 10000);
    return () => clearTimeout(t);
  }, [onRemove]);

  const { isLate, colors, planIcon, headline } = getPlanInfo(notification);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: -50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -50 }}
      layout
      className={`relative flex items-center gap-4 sm:gap-5 text-white p-4 sm:p-5 pr-6 sm:pr-8 rounded-[2rem] min-w-[300px] max-w-[90vw] sm:max-w-md mx-auto pointer-events-auto`}
      style={{
        backgroundColor: isLate ? 'rgba(234,179,8,0.2)' : '#0a0a0a',
        backdropFilter: isLate ? 'blur(12px)' : 'none',
        boxShadow: `0 0 40px ${colors.glow}`,
        border: `2px solid ${colors.border}`
      }}
    >
      {/* Animated Glowing Background Lines/Rays */}
      <div 
        className="absolute inset-0 rounded-[2rem] bg-[length:250%_250%] animate-[pulse_3s_linear_infinite] pointer-events-none" 
        style={{
          background: `linear-gradient(45deg, transparent 25%, ${colors.blur} 50%, transparent 75%)`
        }}
      />
      
      {/* Icon Container */}
      <div 
        className="relative z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex shrink-0 items-center justify-center border border-white/20"
        style={{
          background: `linear-gradient(to bottom right, ${colors.border}, ${colors.glow})`,
          boxShadow: `0 0 20px ${colors.glow}`
        }}
      >
        {/* Animated Icon */}
        <motion.div
           animate={{ rotate: [0, -20, 20, -20, 20, 0] }}
           transition={{ repeat: Infinity, duration: 1.5, repeatDelay: 0.5 }}
           className="text-2xl sm:text-3xl"
        >
          {planIcon === '🔔' ? <Bell className="w-6 h-6 sm:w-7 sm:h-7 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" /> : planIcon}
        </motion.div>
        {/* Ping rings */}
        <span 
          className="absolute inline-flex h-full w-full rounded-2xl opacity-40 animate-ping" 
          style={{ backgroundColor: colors.border }}
        />
      </div>
      
      <div className="relative z-10 flex flex-col pt-1 w-full text-center">
        <div className="flex items-center justify-center gap-1 sm:gap-2 mb-1">
          <span className="flex gap-[2px]">
            <span className="w-1.5 sm:w-2 h-1 skew-x-12 opacity-50" style={{ backgroundColor: colors.border }} />
            <span className="w-1.5 sm:w-2 h-1 skew-x-12 opacity-80" style={{ backgroundColor: colors.border }} />
            <span className="w-1.5 sm:w-2 h-1 skew-x-12" style={{ backgroundColor: colors.border }} />
          </span>
          <h4 className="font-black text-[8px] sm:text-[9px] uppercase tracking-[0.25em] text-white/60">
            {headline}
          </h4>
          <span className="flex gap-[2px]">
            <span className="w-1.5 sm:w-2 h-1 -skew-x-12" style={{ backgroundColor: colors.border }} />
            <span className="w-1.5 sm:w-2 h-1 -skew-x-12 opacity-80" style={{ backgroundColor: colors.border }} />
            <span className="w-1.5 sm:w-2 h-1 -skew-x-12 opacity-50" style={{ backgroundColor: colors.border }} />
          </span>
        </div>
        
        <p className="text-white font-bold text-xs sm:text-sm leading-tight drop-shadow-md">
          {notification.message.replace(/(NOVA VENDA!?|NOVA ASSINATURA VIP!?|RENOVAÇÃO VIP!?|RENOVAÇÃO VIP \(Teste\/Cakto\)!?)/i, '').trim()}
        </p>
      </div>

      <button onClick={onRemove} className="absolute top-2 right-2 sm:top-3 sm:right-3 z-20 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
        <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/70" />
      </button>
    </motion.div>
  );
};

const SaleNotificationOverlay = ({ notifications, onRemove }: { notifications: Notification[], onRemove: (id: string) => void }) => {
  if (notifications.length === 0) return null;
  const latestNotif = notifications[notifications.length - 1];
  const latestId = latestNotif.id;
  const { colors, floatingIcons } = getPlanInfo(latestNotif);

  return createPortal(
    <div className="fixed inset-0 z-[99999] pointer-events-none flex flex-col items-center pt-24 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div key={`bg-${latestId}`} className="absolute inset-0 pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.5, 1, 0.8, 1, 0] }}
            transition={{ duration: 10 }}
            className="absolute inset-0 pointer-events-none"
            style={{ boxShadow: `inset 0 0 150px 60px ${colors.blur}`, mixBlendMode: 'screen' }}
          />
          <motion.div initial={{ y: '100vh', opacity: 0 }} animate={{ y: '-100vh', opacity: [0, 1, 1, 0] }} transition={{ duration: 5, ease: "linear" }} className="absolute left-4 sm:left-10 text-5xl sm:text-7xl opacity-90" style={{ filter: `drop-shadow(0 0 20px ${colors.border})` }}>
            {floatingIcons[0]}
          </motion.div>
          <motion.div initial={{ y: '100vh', opacity: 0 }} animate={{ y: '-100vh', opacity: [0, 1, 1, 0] }} transition={{ duration: 6, ease: "linear", delay: 1 }} className="absolute right-4 sm:right-10 text-5xl sm:text-7xl opacity-90" style={{ filter: `drop-shadow(0 0 20px ${colors.border})` }}>
            {floatingIcons[1]}
          </motion.div>
          <motion.div initial={{ y: '100vh', opacity: 0 }} animate={{ y: '-100vh', opacity: [0, 1, 1, 0] }} transition={{ duration: 4, ease: "linear", delay: 2 }} className="absolute left-[20%] text-4xl sm:text-6xl opacity-80" style={{ filter: `drop-shadow(0 0 15px ${colors.border})` }}>
            {floatingIcons[2]}
          </motion.div>
          <motion.div initial={{ y: '100vh', opacity: 0 }} animate={{ y: '-100vh', opacity: [0, 1, 1, 0] }} transition={{ duration: 5.5, ease: "linear", delay: 0.5 }} className="absolute right-[20%] text-4xl sm:text-6xl opacity-80" style={{ filter: `drop-shadow(0 0 15px ${colors.border})` }}>
            {floatingIcons[3]}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 flex flex-col gap-4 items-center w-full">
        <AnimatePresence>
          {notifications.map(n => (
            <SaleNotificationItem key={n.id} notification={n} onRemove={() => onRemove(n.id)} />
          ))}
        </AnimatePresence>
      </div>
    </div>,
    document.body
  );
};

const isNewAlbumNotif = (n: Notification): boolean => {
  const msg = (n.message || "").toLowerCase();
  const link = (n.link || "").toLowerCase();
  const type = (n.type || "").toLowerCase();

  return (
    type === "new_album" ||
    link.includes("/albums") ||
    link.includes("/shared-album") ||
    msg.includes("álbum") ||
    msg.includes("album") ||
    msg.includes("fotos do") ||
    msg.includes("registros da partida") ||
    msg.includes("suas fotos")
  );
};

const AlbumNotificationItem = ({ 
  notification, 
  totalInQueue, 
  onOpen, 
  onDismiss 
}: { 
  notification: Notification; 
  totalInQueue: number; 
  onOpen: () => void; 
  onDismiss: () => void; 
}) => {
  useEffect(() => {
    // Esconde automaticamente após 15s para não travar a tela caso o usuário esteja longe
    const t = setTimeout(onDismiss, 15000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const coverUrl = notification.metadata?.image_url;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: -60 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -60 }}
      transition={{ type: "spring", stiffness: 350, damping: 25 }}
      onClick={onOpen}
      className="relative flex flex-col sm:flex-row items-center sm:items-stretch gap-4 sm:gap-5 bg-[#080d0a] text-white p-5 sm:p-6 rounded-[2.5rem] shadow-[0_0_55px_rgba(16,185,129,0.7)] border-[2px] border-emerald-500/80 min-w-[320px] max-w-[95vw] sm:max-w-lg mx-auto cursor-pointer group hover:border-emerald-400 hover:shadow-[0_0_75px_rgba(16,185,129,0.9)] transition-all pointer-events-auto"
    >
      {/* Animated Glowing Background Lines/Rays */}
      <div className="absolute inset-0 rounded-[2.5rem] bg-[linear-gradient(45deg,transparent_25%,rgba(16,185,129,0.18)_50%,transparent_75%)] bg-[length:250%_250%] animate-[pulse_3s_linear_infinite] pointer-events-none" />
      
      {/* Botão de Fechar / Próximo da fila (Sem abrir) */}
      <button 
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="absolute top-4 right-4 z-30 text-white/50 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
        title="Fechar notificação"
      >
        <X className="w-4 h-4 stroke-[3]" />
      </button>

      {/* Capa do Álbum / Thumbnail Container */}
      <div className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 rounded-[1.75rem] overflow-hidden bg-gradient-to-br from-emerald-600 to-emerald-950 flex shrink-0 items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.6)] border-[2px] border-emerald-300/40 group-hover:scale-105 transition-transform duration-300">
        {coverUrl ? (
          <>
            <img src={coverUrl} alt="Capa do álbum" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end justify-end p-2">
              <span className="text-sm drop-shadow-md">📸</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="text-3xl animate-bounce">⚽</span>
            <span className="text-[9px] font-black text-emerald-200 tracking-widest uppercase">Álbum</span>
          </div>
        )}
        {/* Ping aura */}
        <span className="absolute inline-flex h-full w-full rounded-[1.75rem] bg-emerald-500 opacity-25 animate-ping pointer-events-none" />
      </div>

      <div className="relative z-10 flex flex-col justify-between flex-1 w-full text-center sm:text-left pt-1 pr-6 sm:pr-4">
        <div>
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-1.5 flex-wrap">
            <span className="bg-gradient-to-r from-emerald-500 to-green-400 text-black font-black text-[9px] uppercase tracking-widest px-2.5 py-0.5 rounded-full shadow">
              ⚽ Novo Álbum
            </span>
            {totalInQueue > 1 && (
              <span className="bg-amber-500/20 border border-amber-400/50 text-amber-300 font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse">
                +{totalInQueue - 1} na fila
              </span>
            )}
          </div>

          <h3 className="bg-gradient-to-r from-emerald-400 via-green-300 to-yellow-300 bg-clip-text text-transparent font-black text-xl sm:text-2xl leading-tight drop-shadow-sm">
            FOTOS LIBERADAS!
          </h3>
          
          <p className="text-white/90 font-bold text-xs sm:text-sm leading-snug mt-1 line-clamp-2">
            {notification.message}
          </p>
        </div>

        <div className="mt-3 flex items-center justify-center sm:justify-start">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-emerald-300 group-hover:text-white transition-colors bg-emerald-500/20 group-hover:bg-emerald-500/40 border border-emerald-500/50 px-3 py-1 rounded-xl">
            Clique para Abrir o Álbum ➔
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const AlbumNotificationOverlay = ({ 
  notifications, 
  onOpen, 
  onDismiss 
}: { 
  notifications: Notification[]; 
  onOpen: (n: Notification) => void; 
  onDismiss: (id: string) => void; 
}) => {
  if (notifications.length === 0) return null;
  // Exibição em fila (um por vez)
  const currentNotif = notifications[0];

  return createPortal(
    <div className="fixed inset-0 z-[99999] pointer-events-none flex flex-col items-center pt-20 sm:pt-24 overflow-hidden">
      {/* Background Stadium Glow & Soccer Ball Animations (Re-triggers per item) */}
      <AnimatePresence mode="wait">
        <motion.div key={`album-bg-${currentNotif.id}`} className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Full screen Stadium Emerald neon border glow */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: [0, 0.9, 0.4, 0.9, 0.6, 0.9, 0] }} 
            transition={{ duration: 12 }}
            className="absolute inset-0 shadow-[inset_0_0_150px_50px_rgba(16,185,129,0.45)] dark:shadow-[inset_0_0_180px_70px_rgba(16,185,129,0.35)] mix-blend-screen pointer-events-none"
          />
          
          {/* Chuva de Bolas de Futebol e Troféus pela tela (Animação estilo Vendas) */}
          <motion.div initial={{ y: '110vh', x: '8vw', rotate: 0, opacity: 0 }} animate={{ y: '-20vh', rotate: 720, opacity: [0, 1, 1, 0] }} transition={{ duration: 4.2, ease: "linear" }} className="absolute text-6xl sm:text-8xl drop-shadow-[0_0_25px_rgba(255,255,255,0.9)] opacity-90">⚽</motion.div>
          <motion.div initial={{ y: '-20vh', x: '82vw', rotate: 0, opacity: 0 }} animate={{ y: '110vh', rotate: -600, opacity: [0, 1, 1, 0] }} transition={{ duration: 3.8, ease: "easeIn", delay: 0.2 }} className="absolute text-7xl sm:text-9xl drop-shadow-[0_0_35px_#10b981] opacity-85">⚽</motion.div>
          <motion.div initial={{ y: '110vh', x: '72vw', rotate: 0, opacity: 0 }} animate={{ y: '-20vh', rotate: 360, opacity: [0, 1, 1, 0] }} transition={{ duration: 4.5, ease: "linear", delay: 0.4 }} className="absolute text-5xl sm:text-7xl drop-shadow-[0_0_25px_#FFD700] opacity-80">⚽</motion.div>
          <motion.div initial={{ y: '-20vh', x: '25vw', rotate: 0, opacity: 0 }} animate={{ y: '110vh', rotate: -720, opacity: [0, 1, 1, 0] }} transition={{ duration: 4, ease: "easeIn", delay: 0.3 }} className="absolute text-6xl sm:text-8xl drop-shadow-[0_0_30px_rgba(255,255,255,0.8)] opacity-90">⚽</motion.div>
          <motion.div initial={{ y: '110vh', x: '42vw', rotate: 0, opacity: 0 }} animate={{ y: '-20vh', rotate: 540, opacity: [0, 1, 1, 0] }} transition={{ duration: 5, ease: "linear", delay: 0.6 }} className="absolute text-5xl sm:text-7xl drop-shadow-[0_0_25px_#10b981] opacity-75">⚽</motion.div>
          <motion.div initial={{ scale: 0, x: '45vw', y: '35vh', rotate: 0 }} animate={{ scale: [0, 2, 0.4, 0], rotate: 360, opacity: [0, 0.8, 0.8, 0] }} transition={{ duration: 3, ease: "easeInOut" }} className="absolute text-8xl sm:text-[11rem] drop-shadow-[0_0_60px_#10b981] opacity-40">⚽</motion.div>
          <motion.div initial={{ y: '-20vh', x: '18vw', rotate: 0, opacity: 0 }} animate={{ y: '110vh', rotate: 360, opacity: [0, 1, 1, 0] }} transition={{ duration: 3.5, ease: "easeIn", delay: 0.7 }} className="absolute text-5xl sm:text-6xl drop-shadow-[0_0_20px_#ffffff] opacity-85">📸</motion.div>
          <motion.div initial={{ y: '110vh', x: '88vw', rotate: 0, opacity: 0 }} animate={{ y: '-20vh', rotate: -360, opacity: [0, 1, 1, 0] }} transition={{ duration: 4, ease: "linear", delay: 0.5 }} className="absolute text-5xl sm:text-7xl drop-shadow-[0_0_25px_#FFD700] opacity-90">🏆</motion.div>
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 flex flex-col gap-4 items-center w-full">
        <AnimatePresence mode="wait">
          <AlbumNotificationItem 
            key={currentNotif.id} 
            notification={currentNotif} 
            totalInQueue={notifications.length}
            onOpen={() => onOpen(currentNotif)}
            onDismiss={() => onDismiss(currentNotif.id)} 
          />
        </AnimatePresence>
      </div>
    </div>,
    document.body
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
 const [notifications, setNotifications] = useState<Notification[]>([]);
 const [unreadCount, setUnreadCount] = useState(0);
 const [open, setOpen] = useState(false);
 const [visibleCount, setVisibleCount] = useState(10);
 const ref = useRef<HTMLDivElement>(null);

 // Prefetched order items cached by order_id
 const [loadedOrderItems, setLoadedOrderItems] = useState<Record<string, any[]>>({});
 // Prefetched order info cached by order_id
 const [loadedOrdersInfo, setLoadedOrdersInfo] = useState<Record<string, { total_amount: number; isVip: boolean }>>({});
 // Selected order details for full screen viewer modal
 const [selectedOrderForModal, setSelectedOrderForModal] = useState<{
 orderId: string;
 buyerName: string;
 message: string;
 } | null>(null);

  // Efeito Sonoro de Moeda (Mario Coin style)
  const playCoinSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = "sine";
      osc.frequency.setValueAtTime(987.77, ctx.currentTime); // B5
      osc.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.1); // E6

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.warn("Áudio bloqueado pelo navegador", e);
    }
  };

  // Efeito Sonoro do Apito de Árbitro de Futebol
  const playWhistleSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const now = ctx.currentTime;

      const playChirp = (startTime: number, duration: number) => {
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        
        // Envelope de volume do sopro
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.4, startTime + 0.03);
        gain.gain.setValueAtTime(0.4, startTime + duration - 0.05);
        gain.gain.linearRampToValueAtTime(0, startTime + duration);

        // Frequências para gerar o batimento de apito de árbitro (Fox 40)
        const freqs = [2600, 2750, 2900];
        freqs.forEach((freq) => {
          const osc = ctx.createOscillator();
          const oscGain = ctx.createGain();
          oscGain.gain.value = 1 / freqs.length;
          
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, startTime);
          osc.frequency.linearRampToValueAtTime(freq + 60, startTime + 0.05);
          osc.frequency.linearRampToValueAtTime(freq - 25, startTime + duration);

          // LFO para vibração rápida da bolinha no apito
          const lfo = ctx.createOscillator();
          const lfoGain = ctx.createGain();
          lfo.type = "sine";
          lfo.frequency.value = 45; // 45 Hz trill
          lfoGain.gain.value = 22;
          lfo.connect(osc.frequency);
          lfo.start(startTime);
          lfo.stop(startTime + duration);

          osc.connect(oscGain);
          oscGain.connect(gain);
          osc.start(startTime);
          osc.stop(startTime + duration);
        });
      };

      // Apito duplo: Peet... Peeeeeeeet!
      playChirp(now, 0.16);
      playChirp(now + 0.23, 0.60);
    } catch (e) {
      console.warn("Áudio do apito bloqueado pelo navegador", e);
    }
  };

  // Efeito Sonoro de Celebração VIP / Novo Assinante (Triumphant Royal Fanfare)
  const playVipFanfareSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const now = ctx.currentTime;
      const notes = [
        { freq: 523.25, time: 0, dur: 0.12 },    // C5
        { freq: 659.25, time: 0.12, dur: 0.12 }, // E5
        { freq: 783.99, time: 0.24, dur: 0.12 }, // G5
        { freq: 1046.50, time: 0.36, dur: 0.7 }  // C6 (Triumphant sustain)
      ];

      notes.forEach((n) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(n.freq, now + n.time);

        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(n.freq * 1.002, now + n.time);
        osc2.connect(gain);

        gain.gain.setValueAtTime(0, now + n.time);
        gain.gain.linearRampToValueAtTime(0.4, now + n.time + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + n.time + n.dur);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + n.time);
        osc.stop(now + n.time + n.dur);
        osc2.start(now + n.time);
        osc2.stop(now + n.time + n.dur);
      });
    } catch (e) {
      console.warn("Áudio VIP bloqueado pelo navegador", e);
    }
  };

  // Efeito Sonoro de Alerta Beep (Para PIX gerado, Abandono de Checkout, etc)
  const playNotificationBeepSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      osc.frequency.setValueAtTime(1108.73, ctx.currentTime + 0.08); // C#6

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    } catch (e) {
      console.warn("Áudio beep bloqueado", e);
    }
  };

  const [saleNotifications, setSaleNotifications] = useState<Notification[]>([]);
  const [albumNotifications, setAlbumNotifications] = useState<Notification[]>([]);

  const removeSaleNotification = (id: string) => {
    setSaleNotifications(prev => prev.filter(n => n.id !== id));
  };

  const removeAlbumNotification = (id: string) => {
    setAlbumNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleOpenAlbumNotification = async (n: Notification) => {
    if (user && !n.read) {
      await supabase.from("notifications").update({ read: true }).eq("id", n.id);
      setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, read: true } : item));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setAlbumNotifications(prev => prev.filter(item => item.id !== n.id));

    if (n.link) {
      if (n.link.startsWith("http://") || n.link.startsWith("https://")) {
        window.location.href = n.link;
      } else {
        navigate(n.link);
      }
    } else {
      navigate("/my-records");
    }
  };

  // Aciona o apito sempre que o item do topo da fila mudar
  useEffect(() => {
    if (albumNotifications.length > 0) {
      playWhistleSound();
    }
  }, [albumNotifications.length > 0 ? albumNotifications[0].id : null]);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      const list = (data || []) as Notification[];
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.read).length || 0);

      const unreadAlbums = list.filter((n) => !n.read && isNewAlbumNotif(n));
      if (unreadAlbums.length > 0) {
        setAlbumNotifications(unreadAlbums);
      }
    };
    fetch();

    const channel = supabase
      .channel("notif-bell")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, (payload) => {
        const n = payload.new as Notification;
        setNotifications((prev) => [n, ...prev].slice(0, 100));
        setUnreadCount((c) => c + 1);
        
        const isVip = n.type === 'vip_upgrade' || ((n.message || '').toUpperCase().includes('VIP') && !(n.message || '').toUpperCase().includes('RECUSAD') && !(n.message || '').toUpperCase().includes('GERADO') && !(n.message || '').toUpperCase().includes('ABANDON'));
        const isSale = n.type === 'sale_complete' || n.type === 'sale_late' || (n.message || '').toUpperCase().includes('VENDA') || (n.message || '').toUpperCase().includes('COMPROU');
        
        if (isNewAlbumNotif(n)) {
          setAlbumNotifications((prev) => [...prev, n]);
        } else {
          // Play appropriate sound
          if (isVip) {
            playVipFanfareSound();
          } else if (isSale) {
            playCoinSound();
          } else {
            playNotificationBeepSound();
          }
          // The user specifically requested all notifications (like PIX Gerado) to appear as full-screen overlays
          setSaleNotifications((prev) => [...prev, n]);
        }
      })
      .subscribe();

    // TEST EVENT LISTENER FOR USER TESTING
    const handleTestEvent = (e: any) => {
      const n = { 
        id: Date.now().toString() + Math.random().toString(), 
        type: 'sale_complete', 
        message: 'NOVA VENDA! VILENA CARDOSO COMPROU 20 MÍDIAS.', 
        read: false 
      } as Notification;
      
      setNotifications((prev) => [n, ...prev].slice(0, 100));
      setUnreadCount((c) => c + 1);
      playCoinSound();
      setSaleNotifications((prev) => [...prev, n]);
    };

    const handleTestAlbumEvent = () => {
      const n = { 
        id: "test-album-" + Date.now().toString() + Math.random().toString(), 
        type: 'new_album', 
        message: 'AS FOTOS DO NOVO ÁLBUM "COPA FUTSAL DE CAMPEÕES" JÁ ESTÃO LIBERADAS!', 
        link: '/albums',
        read: false,
        created_at: new Date().toISOString(),
        metadata: {
          image_url: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=600&auto=format&fit=crop'
        }
      } as Notification;
      
      setNotifications((prev) => [n, ...prev].slice(0, 100));
      setUnreadCount((c) => c + 1);
      setAlbumNotifications((prev) => [...prev, n]);
    };

    window.addEventListener('test_notification', handleTestEvent);
    window.addEventListener('test_album_notification', handleTestAlbumEvent);

    return () => { 
      supabase.removeChannel(channel); 
      window.removeEventListener('test_notification', handleTestEvent);
      window.removeEventListener('test_album_notification', handleTestAlbumEvent);
    };
  }, [user]);

 // Background-fetch order items for visible notifications
 useEffect(() => {
 if (open && notifications.length > 0) {
 notifications.forEach((n) => {
 if (n.type === 'sale_complete' && n.metadata?.order_id) {
 fetchOrderItems(n.metadata.order_id);
 }
 });
 }
 }, [open, notifications]);

 const fetchOrderItems = async (orderId: string) => {
  if (loadedOrderItems[orderId]) return;
  try {
  const [itemsRes, orderRes] = await Promise.all([
    supabase
    .from("order_items")
    .select(`
    id,
    file_url,
    thumbnail_url,
    original_file_url,
    drive_download_url,
    filename,
    media_type,
    album_media:album_media!media_id (
    id,
    filename,
    file_url,
    thumbnail_url,
    original_file_url,
    media_type
    )
    `)
    .eq("order_id", orderId),
    supabase
    .from("orders")
    .select("total_amount, user_id")
    .eq("id", orderId)
    .maybeSingle()
  ]);

  if (itemsRes.error) throw itemsRes.error;
  if (itemsRes.data) {
    setLoadedOrderItems(prev => ({ ...prev, [orderId]: itemsRes.data }));
  }

  if (orderRes.data) {
    let isVip = false;
    if (orderRes.data.user_id) {
      const { data: sub } = await supabase.from("subscriptions").select("id").eq("user_id", orderRes.data.user_id).eq("status", "active").maybeSingle();
      if (sub) isVip = true;
    }
    setLoadedOrdersInfo(prev => ({ ...prev, [orderId]: { total_amount: Number(orderRes.data.total_amount) || 0, isVip } }));
  }
  } catch (err) {
  console.error("Error fetching notification order items:", err);
  }
  };

 useEffect(() => {
 const handleClickOutside = (e: MouseEvent) => {
 if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
 };
 document.addEventListener("mousedown", handleClickOutside);
 return () => document.removeEventListener("mousedown", handleClickOutside);
 }, []);

 const handleOpen = async () => {
 setOpen(!open);
 if (!open && unreadCount > 0 && user) {
 await supabase.from("notifications").update({ read: true }).eq("user_id", user.id).eq("read", false);
 setUnreadCount(0);
 setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
 }
 };

 if (!user) return null;

 return (
 <div className="relative" ref={ref}>
 <button onClick={handleOpen} className="relative text-muted-foreground hover:text-foreground transition-colors">
 <Bell className="w-5 h-5" />
 {unreadCount > 0 && (
 <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
 {unreadCount > 9 ? "9+" : unreadCount}
 </span>
 )}
 </button>

 <AnimatePresence>
 {open && (
 <motion.div
 initial={{ opacity: 0, y: -5, scale: 0.95 }}
 animate={{ opacity: 1, y: 0, scale: 1 }}
 exit={{ opacity: 0, y: -5, scale: 0.95 }}
 className="absolute right-0 top-12 w-[340px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-card/95 backdrop-blur-xl border border-foreground/10 rounded-2xl shadow-2xl z-50 custom-scrollbar origin-top-right mr-[-10px] md:mr-0"
 >
 <div className="p-4 border-b border-foreground/5 sticky top-0 bg-card/90 backdrop-blur-sm z-10">
 <p className="text-sm font-black uppercase tracking-widest text-primary">Notificações</p>
 </div>
 {notifications.length === 0 ? (
 <div className="p-6 text-center text-sm text-muted-foreground">Nenhuma notificação.</div>
 ) : (
 <div className="divide-y divide-border">
 {(() => { const seen = new Set(); return notifications.filter(n => { const meta = typeof n.metadata === "string" ? JSON.parse(n.metadata) : n.metadata; const orderId = meta?.order_id; if (n.type === "sale_complete" && orderId) { if (seen.has(orderId)) return false; seen.add(orderId); } else { if (seen.has(n.id)) return false; seen.add(n.id); } return true; }); })().slice(0, visibleCount).map((n) => {
 const meta = typeof n.metadata === 'string' ? JSON.parse(n.metadata) : n.metadata;
 const orderId = meta?.order_id;
 const items = orderId ? (loadedOrderItems[orderId] || []) : [];
 const firstItem = items[0];
 const firstMedia = firstItem?.album_media || firstItem;
 const displayThumbnailUrl = meta?.image_url || firstMedia?.thumbnail_url || firstMedia?.file_url;

 return (
 <div 
 key={n.id} 
 onClick={() => {
 if (n.type === 'sale_complete' && orderId) {
 setSelectedOrderForModal({
 orderId: orderId,
 buyerName: meta?.buyer_name || "Cliente",
 message: n.message
 });
 setOpen(false);
 } else if (n.link) {
 window.location.href = n.link;
 setOpen(false);
 }
 }}
 className={`p-3 text-sm cursor-pointer hover:bg-foreground/10 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
 >
 <div className="flex gap-3 items-start">
 {displayThumbnailUrl && (
 <div className="w-11 h-11 rounded-lg overflow-hidden shrink-0 border border-foreground/10 bg-neutral-900/50">
 <img 
 src={displayThumbnailUrl} 
 alt="" 
 className="w-full h-full object-cover" 
 />
 </div>
 )}
 <div className="flex-1 min-w-0">
 <p className="text-foreground leading-snug text-[11px]">{n.message}</p>
 
 {/* Direct download list of items inside sininho notification */}
 {n.type === 'sale_complete' && items.length > 0 && (
 <div className="mt-2.5 space-y-1" onClick={(e) => e.stopPropagation()}>
 <p className="text-[8px] font-black uppercase text-primary tracking-wider">Download (Sem Marca)</p>
 <div className="grid grid-cols-5 gap-1 p-1 bg-foreground/10 rounded-lg border border-foreground/10 ">
 {items.map((item) => {
 const media = item.album_media || item;
 const displayUrl = item.thumbnail_url || media.thumbnail_url || item.file_url || media.file_url;
 const downloadUrl = item.original_file_url || media.original_file_url || item.file_url || media.file_url || item.drive_download_url || media.drive_download_url;
 const filename = item.filename || media.filename || "foto";
 const isVideo = item.media_type === "video" || media.media_type === "video";

 return (
 <div key={item.id} className="relative group/notif-img aspect-square rounded overflow-hidden border border-foreground/10 bg-neutral-900/50">
 {displayUrl ? (
 <img src={displayUrl} className="w-full h-full object-cover" alt="" />
 ) : (
 <div className="w-full h-full flex items-center justify-center text-[5px] text-foreground/40 ">Mídia</div>
 )}
 {isVideo && <span className="absolute top-0.5 left-0.5 bg-foreground/10 text-[4px] font-black text-primary px-0.5 py-0.2 rounded">V</span>}
 
 {/* Hover download action inside card */}
 <div className="absolute inset-0 bg-foreground/10 opacity-0 group-hover/notif-img:opacity-100 transition-opacity flex items-center justify-center">
 {downloadUrl && (
 <button
 type="button"
 onClick={async (e) => {
 e.stopPropagation();
 const tid = toast.loading("Iniciando download...");
 try {
 const success = await Promise.race([downloadMedia(downloadUrl, filename), new Promise<boolean>((resolve) => setTimeout(() => resolve(true), 12000))]);
 toast.dismiss(tid);
 if (success) {
 toast.success("Download concluído!", { id: tid });
 } else {
 window.open(downloadUrl, "_blank");
 }
 } catch (err) {
 toast.dismiss(tid);
 window.open(downloadUrl, "_blank");
 }
 }}
 className="w-4 h-4 bg-primary/20 hover:bg-primary/35 rounded flex items-center justify-center text-primary"
 title="Baixar Original"
 >
 <Download className="w-2.5 h-2.5" />
 </button>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}
 {n.type === 'sale_complete' && meta?.filenames && (
 <div className="mt-3 space-y-3 bg-foreground/10 p-2 rounded-lg border border-foreground/10 " onClick={(e) => e.stopPropagation()}>
 <div>
 <p className="text-[9px] font-black text-primary uppercase tracking-widest mb-1">Windows Search (Espaço)</p>
 <div className="flex gap-2">
 <input 
 readOnly 
 value={meta.filenames.join(" ")}
 className="w-full bg-foreground/10 border border-foreground/10 rounded px-2 py-1 text-[10px] text-foreground/40 "
 />
 <button 
 onClick={(e) => {
 e.stopPropagation();
 navigator.clipboard.writeText(meta?.filenames?.join(" ") || "");
 alert("Copiado para Windows!");
 }}
 className="bg-primary/20 text-primary p-1 rounded text-[10px] font-bold"
 >
 Copiar
 </button>
 </div>
 </div>

 <div>
 <p className="text-[9px] font-black text-primary uppercase tracking-widest mb-1">Lightroom Classic (Vírgula)</p>
 <div className="flex gap-2">
 <input 
 readOnly 
 value={meta.filenames.join(", ")}
 className="w-full bg-foreground/10 border border-foreground/10 rounded px-2 py-1 text-[10px] text-foreground/40 "
 />
 <button 
 onClick={(e) => {
 e.stopPropagation();
 navigator.clipboard.writeText(meta?.filenames?.join(", ") || "");
 alert("Copiado para Lightroom!");
 }}
 className="bg-primary/20 text-primary p-1 rounded text-[10px] font-bold"
 >
 Copiar
 </button>
 </div>
 </div>
 </div>
 )}

 <p className="text-[9px] text-muted-foreground mt-1.5 font-medium">
 {new Date(n.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
 </p>
 </div>
 </div>
 </div>
 );
 })}
{notifications.length > visibleCount && (<div className="p-3 flex justify-center"><button type="button" onClick={(e) => { e.stopPropagation(); setVisibleCount((v) => v + 10); }} className="text-xs font-black uppercase tracking-widest text-primary hover:text-primary/80 transition-colors">Ver mais</button></div>)}
 </div>
 )}
 </motion.div>
 )}
 </AnimatePresence>

  {/* Sale Notification Full Screen Overlay */}
  <AnimatePresence>
    <SaleNotificationOverlay 
      notifications={saleNotifications} 
      onRemove={removeSaleNotification} 
    />
  </AnimatePresence>

  {/* Album Notification Full Screen Overlay (Com Apito e Bolas) */}
  <AnimatePresence>
    <AlbumNotificationOverlay
      notifications={albumNotifications}
      onOpen={handleOpenAlbumNotification}
      onDismiss={removeAlbumNotification}
    />
  </AnimatePresence>

 {/* Full screen Visualizer modal overlay for sale items */}
 {typeof document !== 'undefined' && createPortal(
  <AnimatePresence>
  {selectedOrderForModal && (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm">
  <motion.div
  initial={{ opacity: 0, scale: 0.95, y: 20 }}
  animate={{ opacity: 1, scale: 1, y: 0 }}
  exit={{ opacity: 0, scale: 0.95, y: 20 }}
  className="bg-[#181818] border border-foreground/10 rounded-3xl p-4 sm:p-6 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl relative my-auto"
  >
 {/* Close Button */}
 <button
 onClick={() => setSelectedOrderForModal(null)}
 className="absolute top-4 right-4 text-foreground/40 hover:text-foreground transition-colors p-2 rounded-xl bg-foreground/10 hover:bg-foreground/10 z-10"
 >
 <X className="w-5 h-5" />
 </button>

 <div className="flex-shrink-0 mb-4">
 <h3 className="text-lg font-bold text-foreground uppercase tracking-tight">Nova Venda Realizada!</h3>
 <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
    <p className="text-sm font-medium text-white/90 bg-white/10 px-3 py-1.5 rounded-lg w-fit">
      Comprador: <span className="font-black text-primary">{selectedOrderForModal.buyerName}</span>
    </p>
    {loadedOrdersInfo[selectedOrderForModal.orderId] && (
      <>
        <p className="text-sm font-medium text-white/90 bg-white/10 px-3 py-1.5 rounded-lg w-fit">
          Valor: <span className="font-black text-green-400">
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(loadedOrdersInfo[selectedOrderForModal.orderId].total_amount)}
          </span>
        </p>
        {loadedOrdersInfo[selectedOrderForModal.orderId].isVip && (
          <p className="text-xs font-black text-black bg-gradient-to-r from-amber-400 to-amber-600 px-3 py-1.5 rounded-lg w-fit shadow-[0_0_10px_rgba(251,191,36,0.5)] flex items-center gap-1">
            <Crown className="w-3.5 h-3.5" /> ASSINANTE VIP
          </p>
        )}
      </>
    )}
 </div>
 <p className="text-[10px] text-primary/75 font-mono mt-2">ID do Pedido: {selectedOrderForModal.orderId}</p>
 </div>

 <div className="border-t border-foreground/10 pt-4 flex-1 overflow-y-auto custom-scrollbar pr-1">
 <p className="text-xs font-black uppercase text-foreground/40 tracking-wider mb-3">Mídias Disponíveis (Sem Marca D'água)</p>
 
 {(!loadedOrderItems[selectedOrderForModal.orderId] || loadedOrderItems[selectedOrderForModal.orderId].length === 0) ? (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-6 h-6 text-primary animate-spin" />
 </div>
 ) : (
 <div className={`grid gap-4 ${loadedOrderItems[selectedOrderForModal.orderId].length === 1 ? 'grid-cols-1 max-w-[250px] mx-auto' : 'grid-cols-2 sm:grid-cols-3'}`}>
 {loadedOrderItems[selectedOrderForModal.orderId].map((item) => {
 const media = item.album_media || item;
 const displayUrl = item.thumbnail_url || media.thumbnail_url || item.file_url || media.file_url;
 const downloadUrl = item.original_file_url || media.original_file_url || item.file_url || media.file_url || item.drive_download_url || media.drive_download_url;
 const filename = item.filename || media.filename || "foto";
 const isVideo = item.media_type === "video" || media.media_type === "video";

 return (
 <div key={item.id} className="relative group/modal-media rounded-2xl overflow-hidden border border-foreground/10 aspect-square bg-neutral-900 flex flex-col justify-between shadow-lg">
 {displayUrl ? (
 <img src={displayUrl} className="w-full h-full object-cover" alt="" />
 ) : (
 <div className="flex-1 flex items-center justify-center text-xs text-foreground/40 ">Sem Prévia</div>
 )}

 {isVideo && (
 <span className="absolute top-2 left-2 bg-foreground/10 text-[8px] font-black text-primary px-1.5 py-0.5 rounded uppercase">Vídeo</span>
 )}

 {/* Hover Actions */}
 <div className="absolute inset-0 bg-foreground/10 opacity-0 group-hover/modal-media:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3 text-center">
 <p className="text-[10px] font-bold text-foreground truncate w-full" title={filename}>{filename}</p>
 
 <div className="flex gap-2">
 {displayUrl && (
 <a
 href={displayUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="p-2 bg-foreground/10 hover:bg-foreground/10 rounded-xl text-foreground transition-colors"
 title="Visualizar Original"
 >
 <Eye className="w-4 h-4" />
 </a>
 )}
 {downloadUrl && (
 <button
 type="button"
 onClick={async () => {
 const tid = toast.loading("Baixando arquivo original...");
 try {
 const success = await Promise.race([downloadMedia(downloadUrl, filename), new Promise<boolean>((resolve) => setTimeout(() => resolve(true), 12000))]);
 toast.dismiss(tid);
 if (success) {
 toast.success("Download iniciado!", { id: tid });
 } else {
 window.open(downloadUrl, "_blank");
 }
 } catch (err) {
 toast.dismiss(tid);
 window.open(downloadUrl, "_blank");
 }
 }}
 className="p-2 bg-primary/20 hover:bg-primary/30 rounded-xl text-primary transition-colors flex items-center gap-1 font-bold text-[10px]"
 title="Baixar Sem Marca"
 >
 <Download className="w-4 h-4" />
 </button>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>

 <div className="flex justify-end gap-2 border-t border-foreground/10 pt-4">
 <Button
 onClick={() => setSelectedOrderForModal(null)}
 className="bg-foreground/10 hover:bg-foreground/10 text-foreground rounded-xl text-xs px-4"
 >
 Fechar
 </Button>
 <Button
 onClick={() => {
 window.location.href = `/admin?tab=dashboard&order_id=${selectedOrderForModal.orderId}`;
 setSelectedOrderForModal(null);
 }}
 className="bg-primary hover:bg-primary/95 text-foreground rounded-xl text-xs px-4 font-bold"
 >
 Ver no Dashboard
 </Button>
 </div>
 </motion.div>
 </div>
 )}
 </AnimatePresence>,
 document.body
 )}
 </div>
 );
};

export default NotificationBell;
